/**
 * Fallback JavaScript pour animations scroll sur Safari iOS
 * Safari ne supporte pas animation-timeline: scroll()
 *
 * Optimisations appliquées:
 * - LERP (Linear Interpolation) pour des animations ultra-fluides
 * - Boucle rAF continue au lieu d'écouter l'event scroll (évite le lag iOS)
 * - Cache des éléments DOM et propriétés pré-calculées
 * - translate3d pour forcer l'accélération GPU
 */
export default defineNuxtPlugin({
  name: "scroll-animation-fallback",
  enforce: "post",
  setup() {
    // LERP - La clé pour des animations fluides
    const lerp = (start: number, end: number, factor: number): number => {
      return start + (end - start) * factor;
    };

    // Facteur de lissage (0.1 = très smooth, 0.3 = plus réactif)
    const LERP_FACTOR = 0.12;
    const LERP_THRESHOLD = 0.5;

    // Position lissée (interpolée)
    let smoothScrollY = 0;
    let targetScrollY = 0;
    let isAnimating = false;

    // Cache des éléments DOM
    interface CardCache {
      el: HTMLElement;
      exitX: number;
      exitY: number;
      rotation: string;
    }
    let mobileLeftCache: CardCache[] = [];
    let mobileRightCache: CardCache[] = [];

    // Cache viewport
    let vh = 0;
    let vh06 = 0;

    // Helper pour extraire les CSS custom properties
    const getCustomProperty = (
      el: HTMLElement,
      prop: string,
      fallback: string
    ): number => {
      const inlineStyle = el.getAttribute("style") || "";
      const match = inlineStyle.match(new RegExp(`${prop}:\\s*([^;]+)`));
      if (match && match[1]) {
        return parseFloat(match[1]);
      }
      return parseFloat(fallback);
    };

    // Extraire la rotation depuis le style
    const extractRotation = (el: HTMLElement): string => {
      const style = el.getAttribute("style") || "";
      const match = style.match(/rotate\(([^)]+)\)/);
      return match && match[1] ? match[1] : "0deg";
    };

    // Fonction de cache des éléments et propriétés
    const cacheElements = () => {
      const mobileCardsLeft = Array.from(
        document.querySelectorAll(".hero-mobile-card-left")
      ) as HTMLElement[];
      const mobileCardsRight = Array.from(
        document.querySelectorAll(".hero-mobile-card-right")
      ) as HTMLElement[];

      mobileLeftCache = mobileCardsLeft.map((el) => ({
        el,
        exitX: getCustomProperty(el, "--exit-x", "-80"),
        exitY: getCustomProperty(el, "--exit-y", "0"),
        rotation: extractRotation(el),
      }));

      mobileRightCache = mobileCardsRight.map((el) => ({
        el,
        exitX: getCustomProperty(el, "--exit-x", "80"),
        exitY: getCustomProperty(el, "--exit-y", "0"),
        rotation: extractRotation(el),
      }));

      // Appliquer will-change pour promouvoir sur GPU
      const allCards = [...mobileCardsLeft, ...mobileCardsRight];
      allCards.forEach((card) => {
        card.style.willChange = "transform, opacity";
      });
    };

    // Update viewport cache on resize
    const updateViewportCache = () => {
      vh = window.innerHeight;
      vh06 = vh * 0.6;
    };

    // Animation loop avec LERP
    const animate = () => {
      targetScrollY = window.scrollY;

      // Appliquer le lerp pour lisser le mouvement
      smoothScrollY = lerp(smoothScrollY, targetScrollY, LERP_FACTOR);

      // Vérifier si on doit continuer l'animation
      const diff = Math.abs(targetScrollY - smoothScrollY);

      if (diff > LERP_THRESHOLD || targetScrollY !== 0) {
        updateAnimations(smoothScrollY);
        requestAnimationFrame(animate);
      } else {
        isAnimating = false;
      }
    };

    // Démarrer l'animation si pas déjà en cours
    const startAnimation = () => {
      if (!isAnimating) {
        isAnimating = true;
        requestAnimationFrame(animate);
      }
    };

    // Appliquer les transformations basées sur la position lissée
    const updateAnimations = (scrollY: number) => {
      // Mobile cards left
      for (let i = 0; i < mobileLeftCache.length; i++) {
        const item = mobileLeftCache[i];
        if (!item) continue;
        const { el, exitX, exitY, rotation } = item;
        const progress = Math.min(1, Math.max(0, scrollY / vh06));
        el.style.opacity = String(1 - progress);
        el.style.transform = `translate3d(${exitX * progress}px, ${exitY * progress}px, 0) rotate(${rotation})`;
      }

      // Mobile cards right
      for (let i = 0; i < mobileRightCache.length; i++) {
        const item = mobileRightCache[i];
        if (!item) continue;
        const { el, exitX, exitY, rotation } = item;
        const progress = Math.min(1, Math.max(0, scrollY / vh06));
        el.style.opacity = String(1 - progress);
        el.style.transform = `translate3d(${exitX * progress}px, ${exitY * progress}px, 0) rotate(${rotation})`;
      }
    };

    const onScroll = () => {
      startAnimation();
    };

    // Ce fallback JS n'existe que pour Safari, qui ne supporte pas
    // `animation-timeline: scroll()`. Chrome/Firefox (desktop ET Android) l'ont
    // en natif via CSS → inutile d'y faire tourner une boucle rAF qui consomme
    // le main thread (TBT). On détecte Safari et on court-circuite ailleurs.
    // (Audit perf TBT 2026-07-31.)
    const supportsScrollTimeline =
      typeof CSS !== "undefined" &&
      typeof CSS.supports === "function" &&
      CSS.supports("animation-timeline: scroll()");

    const initFallback = () => {
      // Ne pas exécuter sur desktop
      if (window.innerWidth >= 768) {
        return;
      }

      // Navigateur avec support natif (Chrome/Firefox/Android) → le CSS gère
      // l'animation, aucun JS nécessaire. On n'attache aucun listener ni rAF.
      if (supportsScrollTimeline) {
        return;
      }

      setTimeout(() => {
        cacheElements();
        updateViewportCache();

        // Si pas de cartes mobiles, ne rien faire
        if (mobileLeftCache.length === 0 && mobileRightCache.length === 0) {
          return;
        }

        smoothScrollY = window.scrollY;
        targetScrollY = window.scrollY;

        window.addEventListener("scroll", onScroll, { passive: true });
        window.addEventListener("resize", updateViewportCache, {
          passive: true,
        });

        updateAnimations(smoothScrollY);
        startAnimation();
      }, 300);
    };

    // Attendre que le DOM soit prêt
    if (document.readyState === "complete") {
      initFallback();
    } else {
      window.addEventListener("load", initFallback);
    }
  },
});
